import axios from 'axios';
import {
  LOQATE_ADDRESS_FINDER_BASE_URL,
  LOQATE_RETRIEVE_URL,
  LOQATE_FIND_URL,
} from '@/constants/loqate';
import { Ref } from 'vue';
import * as Sentry from '@sentry/vue';
import { watchDebounced } from '@vueuse/core';

interface FindQuery {
  text: string;
  language?: string;
  country?: string;
  limit?: number;
  containerId?: string;
}

export interface FindItem {
  Description: string;
  Highlight?: string;
  Id: string;
  Text: string;
  Error?: number;
  Cause?: string;
  Resolution?: string;
  Type: 'Container' | 'Address';
}

interface FindResponse {
  Items: FindItem[];
}

interface RetrieveResponse {
  Items: {
    Id: string;
    DomesticId: string;
    Language: string;
    LanguageAlternative: string;
    Department: string;
    Company: string;
    SubBuilding: string;
    BuildingNumber: string;
    BuildingName: string;
    SecondaryStree: string;
    Stree: string;
    Block: string;
    Neighbourhood: string;
    District: string;
    City: string;
    Line1: string;
    Line2: string;
    Line3: string;
    Line4: string;
    Line5: string;
    AdminAreaName: string;
    AdminAreaCode: string;
    Province: string;
    ProvinceName: string;
    ProvinceCode: string;
    PostalCode: string;
    CountryName: string;
    CountryIso2: string;
    CountryIso3: string;
    CountryIsoNumber: number;
    SortingNumber1: string;
    SortingNumber2: string;
    Barcode: string;
    POBoxNumber: string;
    Label: string;
    Type: string;
    DataLevel: string;
    Field1: string | null;
  }[];
}

interface Params {
  debounceTimer?: number;
  blockSearch?: Ref<boolean>;
}

const useAddressFinder = ({ debounceTimer = 300, blockSearch }: Params = {}) => {
  let findCache: Record<string, FindResponse> = {};
  let retrieveCache: Record<string, RetrieveResponse> = {};
  const text = ref('');
  const suggestionList = ref<FindItem[]>();
  const client = axios.create({
    baseURL: LOQATE_ADDRESS_FINDER_BASE_URL,
    params: {
      Key: import.meta.env.VITE_LOQATE_API_KEY,
    },
  });
  const retrieve = async (id: string) => {
    if (retrieveCache[id]) {
      return retrieveCache[id];
    }

    const { data } = await client.get<RetrieveResponse>(LOQATE_RETRIEVE_URL, {
      params: {
        Id: id,
        Field1Format: '{PAFDeliveryPointSuffix}',
        cache: true,
      },
    });

    retrieveCache[id] = data;

    return data;
  };

  const find = async (query: FindQuery) => {
    const { text, country = 'GBR', containerId, language = 'en', limit } = query;

    const trimmedText = text.trim();

    // check if text contains only special characters
    const specialCharRegex = /^[^a-zA-Z0-9]+$/;

    if (!trimmedText.length || specialCharRegex.test(trimmedText)) return null;

    const params = {
      Text: trimmedText,
      Countries: country,
      Container: containerId,
      SearchFor: 'Everything',
      OrderBy: 'UserLocation',
      limit,
      LanguagePreference: language,
    };

    const findCacheKey = JSON.stringify(params);

    if (findCache[findCacheKey]) {
      return findCache[findCacheKey];
    }

    const result = await client.get<FindResponse>(LOQATE_FIND_URL, {
      params,
    });

    if (result.data.Items.length) {
      findCache[findCacheKey] = result.data;
    }

    return result.data;
  };

  watchDebounced(
    text,
    async textValue => {
      if (!blockSearch?.value) {
        if (textValue) {
          const foundStuff = await find({ text: textValue, language: 'en-gb', limit: 100 });
          if (textValue === text.value && foundStuff) {
            suggestionList.value = foundStuff.Items.filter(item => {
              if (item.Error !== undefined) {
                Sentry.captureException(new Error(item.Cause));
                Sentry.captureMessage(JSON.stringify(item));
              }
              return item.Error === undefined;
            });
          }
        } else if (suggestionList.value?.length) {
          suggestionList.value = [];
        }
      }
    },
    { debounce: debounceTimer }
  );

  const reset = () => {
    text.value = '';
    suggestionList.value = [];
  };

  const clearCache = () => {
    findCache = {};
    retrieveCache = {};
  };

  return {
    retrieve,
    find,
    text,
    suggestionList,
    reset,
    clearCache,
  };
};

export default useAddressFinder;
